// App.tsx - Route-based code splitting with React.lazy + Suspense
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { useLayoutEffect, useState, useEffect, lazy, Suspense } from 'react';
import { Navigation } from './components/Navigation';
import { Footer } from './components/Footer';
import { SEOHead } from './components/SEOHead';
import { getServiceBySlug } from './data/services';
import WhatsAppButton from './components/WhatsAppButton';
import { ScrollToUp } from './components/ScrollToUp';
import UTMTracker from './components/UTMTracker';

// Eagerly imported: this is the primary landing page, so it should be part of
// the main bundle. Lazy-loading it adds an extra chunk fetch to the critical
// path for the page most visitors and Lighthouse actually score.
import { HomePage } from './components/HomePage';

// Everything else is lazy — loaded only when a visitor actually navigates there.
const PrivacyPolicyPage     = lazy(() => import('./components/PrivacyPolicyPage').then(m => ({ default: m.PrivacyPolicyPage })));
const TermsOfServicePage    = lazy(() => import('./components/TermsOfServicePage').then(m => ({ default: m.TermsOfServicePage })));
const DisclaimerPage        = lazy(() => import('./components/DisclaimerPage').then(m => ({ default: m.DisclaimerPage })));
const FAQPage               = lazy(() => import('./components/FAQPage').then(m => ({ default: m.FAQPage })));
const NotFound              = lazy(() => import('./components/NotFound').then(m => ({ default: m.NotFound })));

const Contact                = lazy(() => import('./contact/Contact').then(m => ({ default: m.Contact })));

const ServicesListPage       = lazy(() => import('./pages/ServicesListPage').then(m => ({ default: m.ServicesListPage })));
const ServicePage            = lazy(() => import('./pages/ServicePage').then(m => ({ default: m.ServicePage })));

const AboutPage               = lazy(() => import('./aboutus/who-we-are/AboutPage').then(m => ({ default: m.AboutPage })));
const WhoWeArePage            = lazy(() => import('./aboutus/our-values/WhoWeArePage').then(m => ({ default: m.WhoWeArePage })));
const Vision2030Page          = lazy(() => import('./aboutus/vision2030/Vision2030Page').then(m => ({ default: m.Vision2030Page })));
const TeamPage                = lazy(() => import('./aboutus/our-team/TeamPage').then(m => ({ default: m.TeamPage })));
const AreasOfPracticePage     = lazy(() => import('./aboutus/areas-of-practice/AreasOfPracticePage').then(m => ({ default: m.AreasOfPracticePage })));
const AccreditationsPage      = lazy(() => import('./aboutus/accreditations/AccreditationsPage').then(m => ({ default: m.AccreditationsPage })));
const InvestSaudiPage         = lazy(() => import('./aboutus/invest-in-saudi-arabia/InvestSaudiPage').then(m => ({ default: m.InvestSaudiPage })));
const OurAssociatesSection    = lazy(() => import('./aboutus/our-association/OurAssociatesSection'));

const AllBlogs                = lazy(() => import('./blogs/AllBlogs').then(m => ({ default: m.AllBlogs })));
const BlogDetail              = lazy(() => import('./blogs/BlogDetail').then(m => ({ default: m.BlogDetail })));
// ─────────────────────────────────────────────────────────────────────────────

// Small inline loader for in-page route transitions. Kept compact (not
// full-viewport) so it doesn't cause a layout jump against the still-visible
// Navigation/Footer around it.
const PageLoader = () => (
  <div
    style={{
      minHeight: '60vh',
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    <div
      style={{
        width: 36,
        height: 36,
        border: '3px solid #e5e7eb',
        borderTopColor: '#c9a84c',
        borderRadius: '50%',
        animation: 'spin 0.7s linear infinite',
      }}
    />
    <style>{`
      @keyframes spin {
        to {
          transform: rotate(360deg);
        }
      }
    `}</style>
  </div>
);


const ScrollToTop = () => {
  const { pathname } = useLocation();
  useLayoutEffect(() => {
    if (pathname !== '/') {
      window.scrollTo(0, 0);
    }
  }, [pathname]);
  return null;
};

const AppContent = () => {
  const location = useLocation();
  const [serviceName, setServiceName] = useState<string>('');

  useEffect(() => {
    const pathWithoutSlash = location.pathname.slice(1);
    const service = getServiceBySlug(pathWithoutSlash);
    if (service) {
      setServiceName(service.shortTitle);
    } else {
      setServiceName('');
    }
  }, [location.pathname]);

  const showMobileBar =
    location.pathname !== '/contact-us' &&
    !location.pathname.includes('/privacy-policy') &&
    !location.pathname.includes('/terms-of-service');

  return (
    <div className="min-h-screen bg-[#faf9f7] flex flex-col">
      <UTMTracker />
      <SEOHead />
      {/* Navigation and Footer stay mounted across route changes — only the
          routed page content below suspends, so the header/footer never
          flash or disappear during navigation. */}
      <Navigation />
      <main className="flex-1">
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/"                        element={<HomePage />} />
            <Route path="/privacy-policy"          element={<PrivacyPolicyPage />} />
            <Route path="/terms-of-service"        element={<TermsOfServicePage />} />
            <Route path="/contact-us"              element={<Contact />} />
            <Route path="/services"                element={<ServicesListPage />} />
            <Route path="/top-Law-firm-in-KSA"     element={<AboutPage />} />
            <Route path="/tamimi-lawfirm-team"     element={<TeamPage />} />
            <Route path="/areas-of-practice"       element={<AreasOfPracticePage />} />
            <Route path="/accreditations"          element={<AccreditationsPage />} />
            <Route path="/vision-2030"             element={<Vision2030Page />} />
            <Route path="/invest-in-saudi-arabia"  element={<InvestSaudiPage />} />
            <Route path="/our-associates"          element={<OurAssociatesSection />} />
            <Route path="/our-value"               element={<WhoWeArePage />} />
            <Route path="/disclaimer"              element={<DisclaimerPage />} />
            <Route path="/faqs"                    element={<FAQPage />} />
            <Route path="/blog"                    element={<AllBlogs />} />
            <Route path="/blog/:slug"              element={<BlogDetail />} />
            <Route path="/:slug"                   element={<ServicePage />} />
            <Route path="*"                        element={<NotFound />} />
          </Routes>
        </Suspense>
      </main>

      <ScrollToUp />
      <WhatsAppButton />
      <Footer />
    </div>
  );
};

export default function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <AppContent />
    </BrowserRouter>
  );
}